import type { ReactNode, ReactElement } from 'react';

import { formatDistanceToNowStrict, format } from 'src/utils/date-fns';
// @mui
import { Avatar, Box, Typography, Stack } from '@mui/material';
import useAuth from 'src/hooks/useAuth';
import useChatClient from 'src/hooks/useChatClient';
import { useTranslation } from 'react-i18next';
import { getS3Url } from 'src/constants';

export interface ChatMessageItemProps {
  message?: Record<string, any>;
  onOpenLightbox?: (...args: any[]) => any;
  [key: string]: any;
}
// components

// ----------------------------------------------------------------------

/**
 * Resolve a message media url: media items store S3 object keys (uploaded
 * through the standard signed-url flow), but absolute URLs pass through
 * untouched.
 */
const resolveMediaUrl = (url) =>
  /^https?:\/\//.test(url) ? url : getS3Url(url);

export default function ChatMessageItem({
  message,
  onOpenLightbox,
}: ChatMessageItemProps) {
  const { t } = useTranslation('translation');
  const { conversationParticipants, activeConversation } = useChatClient();
  const { user } = useAuth();

  const conversationId = activeConversation?.id;
  const participants = conversationParticipants[conversationId] ?? [];

  const messageAuthor = message.authorIdentity;
  const messageBody = message.text;
  const messageDate = message.timestamp;
  const media = Array.isArray(message.media) ? message.media : [];

  const author = participants.find((p) => p.identity === messageAuthor);

  const firstName = message.authorName || author?.name;
  const currentUser =
    user && messageAuthor === `UID${user.id}~TID${user.team_id}`;
  const senderDetails = {
    name: firstName,
  };

  return (
    <Stack
      direction="row"
      justifyContent={currentUser ? 'flex-end' : 'unset'}
      sx={{ mb: 3 }}
    >
      {!currentUser && (
        <Avatar
          alt={senderDetails.name}
          sx={{ width: 32, height: 32, mr: 2 }}
        />
      )}

      <Stack spacing={1} alignItems="flex-end">
        <Typography
          noWrap
          variant="caption"
          sx={{
            color: 'text.disabled',
            ...(!currentUser && {
              mr: 'auto',
            }),
          }}
        >
          {!currentUser ? firstName : t('chat.you')}, &nbsp;
          {formatDistanceToNowStrict(new Date(messageDate), {
            addSuffix: true,
          })}{' '}
          • {format(new Date(messageDate), 'MMM d, h:mm a')}
        </Typography>

        <Stack
          spacing={0.75}
          sx={{
            p: 1.5,
            minWidth: 48,
            maxWidth: 320,
            borderRadius: 1,
            overflow: 'hidden',
            typography: 'body2',
            bgcolor: 'background.neutral',
            ...(currentUser && {
              color: 'grey.800',
              bgcolor: 'primary.lighter',
            }),
          }}
        >
          {messageBody ? <Box component="span">{messageBody}</Box> : null}
          {media.map((item, index) => {
            const src = resolveMediaUrl(item.url);
            return (
              <Box
                key={`${message.id}-media-${index}`}
                component="img"
                alt={item.filename || 'attachment'}
                src={src}
                onClick={() => {
                  if (onOpenLightbox) {
                    onOpenLightbox(src);
                  } else {
                    window.open(src, '_blank', 'noopener');
                  }
                }}
                sx={{
                  borderRadius: 1,
                  maxWidth: '100%',
                  maxHeight: 240,
                  objectFit: 'cover',
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              />
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
}
